import React, { useState, useLayoutEffect, useEffect } from 'react';
import { ExtendReactlytics } from '@americanexpress/reactlytics';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { getSelectedProduct, getProductRegistryById, getProductAccountKey } from '@americanexpress/product-ducks';
import { generateUrls } from '@americanexpress/axp-benefits-utils';
import { browserHistory } from '@americanexpress/one-app-router';
import { fromJS } from 'immutable';
import { BackHeader } from '../../shared/components/BackHeader';
import useOneData from '../../../hooks/useOneData';
import { getEndpoint, getReadBenefitsRequestBody } from '../../../api/api-utils';
import { ReadLoyaltyBenefitsVx } from '../../../constants';
import { Loading } from '../../shared/components/loading';
import { setProductSelected, redirectToCenturionLogin, redirectToMycaLogin } from '../../view-all/utils';
import CardBenefitsTile from '../../view-all/components/card-benefits-tile/CardBenefitsTile';

export function DetailsPage(props) {
  const accountToken = props?.accountToken;
  const parameters = props?.params || {};
  const location = props?.location;
  const pathname = location?.pathname?.toLowerCase();
  const benefitNameFromParam = parameters?.benname;
  const env = props?.env;
  const cardNameValueToUse = props?.cardNameValueToUse;
  const login = props?.login;
  const loggedOut = !login;
  const accountKey = props?.accountKey;
  const faasApiUrl = props?.faasApiUrl;
  const endpoint = getEndpoint(faasApiUrl, ReadLoyaltyBenefitsVx);
  const productType = props?.productType;
  const locale = props?.locale;
  const requestBody = getReadBenefitsRequestBody(accountToken, cardNameValueToUse, login, productType, locale);

  const endpointOptions = {
    body: requestBody,
  };

  const [hidePage, setHidePage] = useState(false);

  const {
    isLoading: loadingReadLoyaltyBenefits,
    data: readLoyaltyBenefits,
    run: runBenefits,
    error: readLoyaltyBenefitsError,
  } = useOneData(endpoint, endpointOptions);

  const loggedInOnly = readLoyaltyBenefits?.cardProduct?.loggedInOnly;

  const [benefits, setBenefits] = useState(readLoyaltyBenefits?.benefits || {});
  const refreshBenefitsData = (newBenefitsData) => setBenefits({ ...newBenefitsData });

  // Sync local state with API data
  useEffect(() => {
    if (readLoyaltyBenefits?.benefits) {
      setBenefits(readLoyaltyBenefits.benefits);
    }
  }, [readLoyaltyBenefits?.benefits]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });
  }, []);

  useLayoutEffect(() => {
    const missingBenefitsTitle = readLoyaltyBenefits && !readLoyaltyBenefits?.benefits[benefitNameFromParam]?.benefitTitle;

    if (missingBenefitsTitle && login && global.BROWSER) {
      const loggedInCardSwitchRedirection = `/card-benefits/view-all?account_key=${accountKey}`;
      browserHistory.push(loggedInCardSwitchRedirection);
    }

    if (missingBenefitsTitle && loggedOut && global.BROWSER) {
      const loggedOutCardSwitchRedirection = `/card-benefits/view-all/${cardNameValueToUse}`;
      browserHistory.push(loggedOutCardSwitchRedirection);
    }
  }, [accountKey, login, readLoyaltyBenefits]);

  /**
   * Certain benefit detail pages are gated behind authentication.
  */
  useLayoutEffect(() => {
    const detailRequiresLogin = readLoyaltyBenefits && readLoyaltyBenefits?.benefits[benefitNameFromParam]?.detailRequiresLogin;
    const isLoggedInOnlyView = Boolean(loggedOut && detailRequiresLogin);
    if (isLoggedInOnlyView && global.BROWSER) {
      setHidePage(true);
      const DETAIL_PAGE = encodeURIComponent(`${window?.location?.href}`);
      window.location.replace(`${generateUrls(env)?.MYCA_LOGIN_URL}${DETAIL_PAGE}`);
    }
  }, [login, readLoyaltyBenefits]);

  useLayoutEffect(() => {
    if (loggedInOnly && loggedInOnly === 'true') {
      if (loggedOut) {
        /*
              if user is going to a centurion detail page while not logged in.
              They are redirected to the centurion login hub
              If not then they are sent to standard MYCA Login
        */
        if (cardNameValueToUse?.includes('centurion')) {
          redirectToCenturionLogin(props.CENTURION_LOGIN_URL);
        } else if (!cardNameValueToUse?.includes('platinum')) {
          redirectToMycaLogin(props.MYCA_LOGIN_URL, pathname);
        }
        /*
          This prevents a user from navigating to a card they should noe be abe to
          see while logged in on their view all page
        */
      } else if (pathname.includes('view-all') && pathname.includes(cardNameValueToUse?.toLowerCase())) {
        push('/card-benefits/view-all');
      }
    } else if (locale !== 'en-US' && loggedOut) {
      redirectToMycaLogin(props.MYCA_LOGIN_URL, pathname);
    }
  }, [login]);

  if (loadingReadLoyaltyBenefits || hidePage) {
    return <Loading />;
  }

  const header = readLoyaltyBenefits?.benefits[parameters?.benname]?.benefitTitle;
  const backHeaderURL = !login ? `/card-benefits/view-all/${cardNameValueToUse}` : '/card-benefits/view-all';
  const actionTag = `${cardNameValueToUse}>>details`;
  const parseEntities = (txt) => new DOMParser().parseFromString(txt, 'text/html').body.textContent;

  // Set url param for selected card or banking product
  let productSelected = '';
  if (login) {
    productSelected = setProductSelected(props?.productRegistry);
  }

  return (
    <div data-testid="details-page">
      <ExtendReactlytics
        analytics={{ event: { meta: { benefitname: parseEntities(header)?.replace(/ /g, '-').toLowerCase() } } }}
      >
        <BackHeader
          title={header}
          url={backHeaderURL}
          actionTag={actionTag}
          reactlytics={{ element: parseEntities(actionTag)?.replace(/ /g, '-').toLowerCase(), type: 'impression' }}
        />
      </ExtendReactlytics>
      <div role="region" aria-label="Benefit Details Section">
        <CardBenefitsTile
          view="detail"
          benefitsData={benefits}
          benefitName={benefitNameFromParam}
          benefit={benefits?.[parameters?.benname]}
          loadedWithErrors={readLoyaltyBenefitsError}
          params={parameters}
          cardname={cardNameValueToUse}
          login={login}
          accountToken={props?.accountToken}
          productSelected={productSelected}
          showTerms={true}
          runBenefits={runBenefits}
          refreshBenefitsData={refreshBenefitsData}
        />
      </div>
    </div>
  );
}

export function mapStateToProps(state) {
  const env = fromJS(state).getIn(['config', 'localeFilename']);
  const product = getSelectedProduct(state);
  return {
    CENTURION_LOGIN_URL: generateUrls(env).CENTURION_LOGIN_URL,
    MYCA_LOGIN_URL: generateUrls(env).MYCA_LOGIN_URL,
    domain: fromJS(state).getIn(['config', 'domainGlobal']),
    login: Boolean(getSelectedProduct(state)),
    accountToken: getSelectedProduct(state),
    accountKey: getProductAccountKey(state),
    productRegistry: getProductRegistryById(state, getSelectedProduct(state)),
    productType: getProductRegistryById(state, product)?.type,
    locale: fromJS(state).getIn(['intl', 'activeLocale']),
  };
}

export default connect(mapStateToProps, null)(DetailsPage);
